import { healthCheckServices, v1AppConfigGetLoginBranding } from '@repo/sdk/gen'
import { captureException } from '@sentry/react'
import { type QueryClient, queryOptions } from '@tanstack/react-query'
import { resolveBrand } from '@/lib/brand'
import { resolveSession } from '@/lib/session-resolver'
import { makeQueryKeys } from '@/query/query-keys'

/**
 * THE SESSION CONTRACT for the operator artifact. The single interface every
 * surface in this app reads identity and current organization from.
 *
 * IT CARRIES NO ROLE, WHERE THE CLIENT'S CONTRACT CARRIES ONE. On this
 * artifact a session IS the operator identity, so the field could only ever
 * hold one value; `lib/session-resolver.ts` holds the reasoning, and it is not
 * repeated here so the two cannot drift.
 *
 * NEW IN MS2. Unlike the client app there was nothing to extend here: MS0 took
 * the back-office session away with the credential stack, `src/query/` held
 * only `query-keys.ts`, and the private layout threw before it could render.
 *
 * It is a DISCRIMINATED UNION on `sessionType`, not an object of optional
 * fields that must co-vary (`architecture.md`). An authenticated session
 * without an organization is not a state this type can express, so a consumer
 * that has narrowed to the authenticated arm has both values or neither.
 *
 * The value comes from `lib/session-resolver.ts`, which was the only file MS4
 * T4.6 (OPSSTD-4691) touched. The shape here did not change.
 *
 * THE SUBSTITUTION is recorded in full on the end-user app's contract, and it
 * ran here unchanged — one file, same signature, same two arms. It is not
 * repeated so the two cannot drift into disagreeing about how their own
 * replacement worked.
 *
 * The one thing that differs: T4.6 gave this artifact its own `/login`, and it
 * collects no organization slug. The operator authenticates against Keycloak's
 * single `master` realm, so that route starts the OIDC round trip
 * straight away, where the end-user app has to resolve an organization first.
 * The root is no longer a dead end either — it redirects to `/operator`.
 */
export function adminSessionQueryOptions() {
	return queryOptions({
		/** Keep the CSRF token available while the session cookie is valid. */
		gcTime: Number.POSITIVE_INFINITY,
		/** Refetched only via auth invalidation or the 401 cache clear, never per navigation. */
		staleTime: Number.POSITIVE_INFINITY,
		queryKey: makeQueryKeys.auth.session(),
		queryFn: ({ client }) =>
			resolveSession(
				resolveBrand(
					client.getQueryData(instanceBrandingQueryOptions().queryKey)
				).productName ?? ''
			)
	})
}

/**
 * The health of the instance's own dependencies, as the console reads it.
 *
 * ## What it is for, and what it deliberately is not
 *
 * One consumer today: the organization detail page, which shows a warning
 * beside a first administrator's invitation that is still waiting while the
 * instance's mail is broken (OPSSTD-5598). Without it the page shows an
 * invitation `pending` with nothing sent, which reads exactly like an
 * invitation that arrived and has not been accepted — the reading the whole
 * section exists to prevent.
 *
 * **It answers about the instance, never about a message.** "Mail is broken
 * now" is a different sentence from "this invitation failed on the 24th", and
 * the second one has nowhere to come from: an invitation records no delivery
 * outcome. The screen must not imply otherwise, and the amendment of 27/08 on
 * OPSSTD-5260 says so in as many words.
 *
 * ## Why it polls, and why gently
 *
 * The server re-probes every sixty seconds, so a faster interval would return
 * the same answer with a newer timestamp. Matching that cadence is what makes
 * the warning appear on its own after a relay dies, and — the half that gets
 * forgotten — **disappear on its own once it is fixed**, which is a criterion
 * of the issue rather than a nicety.
 *
 * ## The route publishes the cause and nothing else
 *
 * Since OPSSTD-5597 the diagnosis carries `cause` alone: it is a public route,
 * and its details had grown to name a realm, an organization id and the
 * instance's own relay host. `cause` is what a consumer should branch on
 * anyway — it is the stable code — and the depth lives in the instance logs.
 */
export function instanceHealthQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.instance.health(),
		queryFn: async (queryContext) => {
			// No `data` envelope on this one: `/health-check/services` is
			// unversioned infrastructure and answers with the object itself,
			// where every `/v1` route wraps its payload.
			return await healthCheckServices({ signal: queryContext.signal })
		},
		refetchInterval: INSTANCE_HEALTH_POLL_MS,
		// A console left open overnight should not show yesterday's verdict the
		// instant it is focused again.
		refetchOnWindowFocus: true
	})
}

/** The server re-probes on this cadence; reading faster buys nothing. */
const INSTANCE_HEALTH_POLL_MS = 60_000

/**
 * The branding published for this instance (read live from the instance
 * bucket by the Sync Server): product name and marks (`LOGIN-BRANDING.md`, "Delivery"). Public, so it
 * needs no session and can be read before sign-in.
 *
 * The cache holds the `instance` block alone: `version` answers a
 * question about the request, not about the brand.
 *
 * STALE FOR SIXTY SECONDS, the endpoint's own `max-age`: a published change
 * reaches an open tab about as fast as it reaches the login page.
 *
 * A FAILED READ IS NOT CACHED AND NEVER BREAKS A PAGE. `retry: false` and the
 * three-second cap keep an unreachable Sync Server from holding the first
 * paint, and the throw stays in the query so nothing dehydrates a failure as
 * if it were an answer. The consumers draw nothing branded through
 * `resolveBrand`; the root loader reports the failure and swallows it.
 *
 * WARMED BY THE ROOT ROUTE'S LOADER, so the first render already has it. It is
 * still read with `useQuery` and not a suspense hook: the warm may have
 * failed, and then `data` is `undefined` and nothing branded is drawn.
 */
export function instanceBrandingQueryOptions() {
	return queryOptions({
		queryKey: makeQueryKeys.appConfig.loginBranding(),
		staleTime: INSTANCE_BRANDING_STALE_MS,
		retry: false,
		queryFn: async ({ signal }) => {
			const response = await v1AppConfigGetLoginBranding({
				signal: AbortSignal.any([
					signal,
					AbortSignal.timeout(INSTANCE_BRANDING_TIMEOUT_MS)
				])
			})
			return response.instance
		}
	})
}

const INSTANCE_BRANDING_STALE_MS = 60 * 1000
const INSTANCE_BRANDING_TIMEOUT_MS = 3000

/**
 * Puts the instance branding in the cache before a route renders, and never
 * lets that fail a page: a failed read is reported and the caller carries on
 * with nothing branded. Stale data is served at once and refreshed behind it.
 *
 * Called from the root loader, which is the one that works when the server can
 * reach the API (an SSR profile), and from the loader of every route that
 * renders in the browser alone — the private shell and the public pages. The
 * SPA shell is prerendered at build time with no API to read, so the browser
 * is the only place its first paint can get the runtime brand from.
 */
export function warmInstanceBranding(queryClient: QueryClient) {
	return queryClient
		.ensureQueryData({
			...instanceBrandingQueryOptions(),
			revalidateIfStale: true
		})
		.catch((error: unknown) => {
			captureException(error)
			return undefined
		})
}
